import { useEffect, useState, useCallback } from "react";
import { api, API, formatApiError, getAccessToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Users,
  UserCheck,
  Clock,
  CheckCircle2,
  XCircle,
  Crown,
  BookOpen,
  CreditCard,
  Wallet,
  Trash2,
  FileText,
  Image as ImageIcon,
  RefreshCw,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  ShieldCheck,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { AdminMentorSubscriptionsPanel } from "../components/AdminMentorSubscriptionsPanel";

const TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "pending", label: "Pending Approvals", icon: Clock },
  { key: "teachers", label: "All Mentors", icon: UserCheck },
  { key: "users", label: "All Users", icon: Users },
  { key: "bookings", label: "Bookings", icon: BookOpen },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "payouts", label: "Mentor Payouts", icon: Wallet },
  { key: "wallets", label: "Wallets", icon: Wallet },
  { key: "mentor_subs", label: "Mentor Plans", icon: ShieldCheck },
  { key: "reports", label: "Session Reports", icon: AlertTriangle },
];

function inr(n) {
  return typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : "—";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function StatCard({ label, value, hint, color = "#13A699", testId }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-black/5" data-testid={testId || `stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-xs font-bold uppercase" style={{ color: `${color}AA` }}>{label}</div>
      <div className="text-2xl font-bold mt-1 font-display" style={{ color }}>{value}</div>
      {hint && <div className="text-[11px] mt-0.5" style={{ color: `${color}99` }}>{hint}</div>}
    </div>
  );
}

function Badge({ status }) {
  const map = {
    approved: { bg: "#13A699", fg: "#FFF7ED", label: "Approved" },
    pending: { bg: "#FFD708", fg: "#13A699", label: "Pending" },
    rejected: { bg: "#DC2626", fg: "#FFF", label: "Rejected" },
    paid: { bg: "#13A699", fg: "#FFF7ED", label: "Paid" },
    signature_failed: { bg: "#DC2626", fg: "#FFF", label: "Signature Failed" },
    confirmed: { bg: "#13A699", fg: "#FFF7ED", label: "Confirmed" },
  };
  const s = map[status] || { bg: "#E5E7EB", fg: "#374151", label: status || "—" };
  return (
    <span
      className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

async function fetchDocAsUrl(teacherId, docType) {
  const token = getAccessToken();
  const res = await fetch(`${API}/admin/teacher/${teacherId}/document/${docType}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to load ${docType}`);
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

function TeacherCard({ teacher, onApprove, onReject, onToggleFeatured, onDelete, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [docs, setDocs] = useState({});
  const [loadingDoc, setLoadingDoc] = useState(null);
  const [docStatus, setDocStatus] = useState(null); // {photo:{ok,error}, aadhar:..., degree:...}
  const [checkingStatus, setCheckingStatus] = useState(false);

  const checkDocs = useCallback(async () => {
    setCheckingStatus(true);
    try {
      const { data } = await api.get(`/admin/teacher/${teacher.id}/documents/check`);
      setDocStatus(data);
    } catch (e) {
      // Silent — UI falls back to plain buttons
    } finally {
      setCheckingStatus(false);
    }
  }, [teacher.id]);

  useEffect(() => {
    if (expanded && !docStatus) checkDocs();
  }, [expanded, docStatus, checkDocs]);

  const loadDoc = async (docType) => {
    if (docs[docType]) {
      window.open(docs[docType].url, "_blank", "noopener,noreferrer");
      return;
    }
    setLoadingDoc(docType);
    try {
      const data = await fetchDocAsUrl(teacher.id, docType);
      setDocs((prev) => ({ ...prev, [docType]: data }));
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingDoc(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-4" data-testid={`admin-teacher-${teacher.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg font-bold" style={{ color: "#13A699" }}>
              {teacher.name || "(no name)"}
            </div>
            <Badge status={teacher.approval_status} />
            {teacher.featured && (
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "#FFD708", color: "#13A699" }}>
                <Crown className="h-3 w-3" /> Featured
              </span>
            )}
          </div>
          <div className="text-xs mt-0.5" style={{ color: "#13A699AA" }}>{teacher.email}</div>
          <div className="text-xs mt-1" style={{ color: "#13A699" }}>
            {teacher.location || "No location"} · ₹{teacher.fees_per_hour || 0}/hr · {teacher.experience_years || 0} yrs exp
          </div>
          {teacher.subjects?.length > 0 && (
            <div className="text-xs mt-1" style={{ color: "#13A69999" }}>
              Subjects: {teacher.subjects.slice(0, 5).join(", ")}{teacher.subjects.length > 5 ? ` +${teacher.subjects.length - 5}` : ""}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {teacher.approval_status !== "approved" && (
            <button
              onClick={() => onApprove(teacher)}
              className="btn-yellow text-xs px-3 py-1.5 inline-flex items-center gap-1"
              data-testid={`approve-btn-${teacher.id}`}
            >
              <CheckCircle2 className="h-3 w-3" /> Approve
            </button>
          )}
          {teacher.approval_status !== "rejected" && (
            <button
              onClick={() => setShowRejectInput((v) => !v)}
              className="text-xs px-3 py-1.5 rounded-full border border-red-600 text-red-600 inline-flex items-center gap-1 hover:bg-red-50"
              data-testid={`reject-btn-${teacher.id}`}
            >
              <XCircle className="h-3 w-3" /> Reject
            </button>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs px-3 py-1.5 rounded-full border border-black/10 inline-flex items-center gap-1"
            style={{ color: "#13A699" }}
            data-testid={`details-btn-${teacher.id}`}
          >
            {expanded ? "Hide" : "Details"}
          </button>
        </div>
      </div>

      {showRejectInput && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`reject-note-wrap-${teacher.id}`}>
          <input
            type="text"
            placeholder="Reason (sent to mentor via email)"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            className="flex-1 min-w-[200px] rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-400 bg-white"
            data-testid={`reject-note-input-${teacher.id}`}
          />
          <button
            onClick={() => {
              onReject(teacher, rejectNote);
              setShowRejectInput(false);
              setRejectNote("");
            }}
            className="text-xs px-3 py-2 rounded-full bg-red-600 text-white font-bold"
            data-testid={`reject-confirm-${teacher.id}`}
          >
            Confirm Reject
          </button>
        </div>
      )}

      {expanded && (
        <div className="mt-4 pt-4 border-t border-black/5 space-y-3" data-testid={`details-panel-${teacher.id}`}>
          <div>
            <div className="text-xs font-bold uppercase" style={{ color: "#13A699AA" }}>Bio</div>
            <div className="text-sm mt-1" style={{ color: "#13A699" }}>{teacher.bio || "(empty)"}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase" style={{ color: "#13A699AA" }}>Qualifications</div>
            <div className="text-sm mt-1" style={{ color: "#13A699" }}>{teacher.qualifications || "(empty)"}</div>
          </div>
          {teacher.phone && (
            <div>
              <div className="text-xs font-bold uppercase" style={{ color: "#13A699AA" }}>Phone</div>
              <div className="text-sm mt-1" style={{ color: "#13A699" }}>{teacher.phone}</div>
            </div>
          )}
          <div>
            <div className="text-xs font-bold uppercase mb-1 flex items-center gap-2" style={{ color: "#13A699AA" }}>
              <span>Documents</span>
              {checkingStatus && <span className="text-[10px] normal-case" style={{ color: "#13A69988" }}>checking storage…</span>}
              {!checkingStatus && docStatus && (
                <button
                  type="button"
                  onClick={() => { setDocStatus(null); checkDocs(); }}
                  className="text-[10px] normal-case underline hover:no-underline"
                  style={{ color: "#13A699" }}
                  data-testid={`recheck-docs-${teacher.id}`}
                >
                  re-check
                </button>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {["photo", "aadhar", "degree"].map((doc) => {
                const hasDoc = !!teacher.documents?.[doc];
                const status = docStatus?.[doc];
                const fetchable = status?.ok === true;
                const broken = status && status.ok === false && hasDoc;
                return (
                  <div key={doc} className="flex flex-col">
                    <button
                      disabled={!hasDoc || loadingDoc === doc}
                      onClick={() => loadDoc(doc)}
                      className={`text-xs px-3 py-2 rounded-xl border inline-flex items-center gap-1.5 ${
                        hasDoc
                          ? "border-[#13A699]/30 text-[#13A699] hover:bg-[#FFF7ED]"
                          : "border-black/10 text-black/40 cursor-not-allowed"
                      }`}
                      data-testid={`doc-${doc}-${teacher.id}`}
                      title={status?.error || (fetchable ? "Reachable in storage" : undefined)}
                    >
                      {/* Status dot */}
                      {hasDoc && status && (
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${
                            fetchable ? "bg-green-500" : "bg-red-500"
                          }`}
                          aria-hidden="true"
                          data-testid={`doc-status-dot-${doc}-${teacher.id}`}
                          data-status={status?.status}
                        />
                      )}
                      {doc === "photo" ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                      {doc.charAt(0).toUpperCase() + doc.slice(1)}
                      {!hasDoc && " (missing)"}
                      {loadingDoc === doc && " …"}
                    </button>
                    {broken && (
                      <span
                        className="text-[10px] mt-0.5 inline-flex items-center gap-1"
                        style={{ color: "#DC2626" }}
                        data-testid={`doc-status-err-${doc}-${teacher.id}`}
                      >
                        <AlertTriangle className="h-3 w-3" /> {status.error}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-2">
            <button
              onClick={() => onToggleFeatured(teacher, teacher.featured ? 0 : 30)}
              className="text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1 font-bold"
              style={{ background: teacher.featured ? "#F3F4F6" : "#FFD708", color: "#13A699" }}
              data-testid={`toggle-featured-${teacher.id}`}
            >
              <Crown className="h-3 w-3" />
              {teacher.featured ? "Remove Featured" : "Make Featured (30d)"}
            </button>
            <button
              onClick={() => onDelete(teacher)}
              className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white inline-flex items-center gap-1 font-bold"
              data-testid={`delete-teacher-${teacher.id}`}
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>
          {teacher.approval_note && (
            <div className="text-xs p-2 rounded-lg bg-[#FFF7ED]" style={{ color: "#13A699" }}>
              <span className="font-bold">Last admin note:</span> {teacher.approval_note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("analytics");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [teachers, setTeachers] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutsSummary, setPayoutsSummary] = useState(null);
  const [payoutStatus, setPayoutStatus] = useState("pending");
  const [courseCancels, setCourseCancels] = useState([]);
  const [courseCancelsSummary, setCourseCancelsSummary] = useState(null);
  const [courseCancelStatus, setCourseCancelStatus] = useState("pending");
  const [wallets, setWallets] = useState([]);
  const [settlementReqs, setSettlementReqs] = useState([]);
  const [settlementStatus, setSettlementStatus] = useState("pending");
  const [walletRoleFilter, setWalletRoleFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [userFilter, setUserFilter] = useState({ role: "all", q: "" });

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load stats");
    }
  }, []);

  const loadAnalytics = useCallback(async (days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics?days=${days}`);
      setAnalytics(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeachers = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/teachers${status ? `?status=${status}` : ""}`);
      setTeachers(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load mentors");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userFilter.role !== "all") params.set("role", userFilter.role);
      if (userFilter.q) params.set("q", userFilter.q);
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [userFilter]);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payments");
      setPayments(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, sum, cc, ccSum] = await Promise.all([
        api.get(`/admin/payouts?status=${payoutStatus}`),
        api.get(`/admin/payouts/summary`),
        api.get(`/admin/course-cancellations?status=${courseCancelStatus}`),
        api.get(`/admin/course-cancellations/summary`),
      ]);
      setPayouts(rows.data || []);
      setPayoutsSummary(sum.data || null);
      setCourseCancels(cc.data || []);
      setCourseCancelsSummary(ccSum.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }, [payoutStatus, courseCancelStatus]);

  const handleMarkReleased = async (row) => {
    if (!window.confirm(`Mark ₹${row.payout_amount} payout to ${row.mentor?.name || "mentor"} as released?`)) return;
    try {
      await api.post(`/admin/payouts/${row.booking_id}/mark-released`, { note: "" });
      toast.success("Payout marked as released");
      loadPayouts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleMarkCharged = async (row) => {
    if (!window.confirm(`Mark ₹${row.course_charge_amount} charge from ${row.mentor?.name || "mentor"} as COLLECTED?`)) return;
    try {
      await api.post(`/admin/course-cancellations/${row.enrollment_id}/mark-collected`, { note: "" });
      toast.success("Course charge marked as collected");
      loadPayouts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const loadWallets = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        api.get(`/admin/wallets${walletRoleFilter ? `?role=${walletRoleFilter}` : ""}`),
        api.get(`/admin/settlement-requests?status=${settlementStatus}`),
      ]);
      setWallets(w.data || []);
      setSettlementReqs(s.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load wallets");
    } finally {
      setLoading(false);
    }
  }, [walletRoleFilter, settlementStatus]);

  const [reports, setReports] = useState([]);
  const [reportsFilter, setReportsFilter] = useState("open");
  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/admin/session-reports${reportsFilter ? `?status=${reportsFilter}` : ""}`,
      );
      setReports(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load session reports");
    } finally {
      setLoading(false);
    }
  }, [reportsFilter]);

  const handleTopup = async (row) => {
    const amt = parseInt(window.prompt(`Top-up amount (₹) for ${row.name || row.email}:`) || "0", 10);
    if (!amt || amt <= 0) return;
    const note = window.prompt("Note for this top-up (optional):") || "";
    try {
      await api.post(`/admin/wallets/${row.user_id}/topup`, { amount: amt, note });
      toast.success(`₹${amt} credited to ${row.name || row.email}`);
      loadWallets();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Top-up failed");
    }
  };

  const handleMarkSettlementPaid = async (req) => {
    if (!window.confirm(`Mark ₹${req.amount} settlement to ${req.mentor_name || req.mentor_email} as PAID?\nMake sure you've completed the bank transfer first.`)) return;
    const note = window.prompt("Reference / UTR (optional):") || "";
    try {
      await api.post(`/admin/settlement-requests/${req.id}/mark-paid`, { note });
      toast.success("Settlement marked as paid");
      loadWallets();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleRejectSettlement = async (req) => {
    const note = window.prompt(`Reason for rejecting ₹${req.amount} settlement:`);
    if (note === null) return;
    try {
      await api.post(`/admin/settlement-requests/${req.id}/reject`, { note });
      toast.success("Settlement rejected");
      loadWallets();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (tab === "analytics") loadAnalytics(analyticsDays);
    else if (tab === "pending") loadTeachers("pending");
    else if (tab === "teachers") loadTeachers("all");
    else if (tab === "users") loadUsers();
    else if (tab === "bookings") loadBookings();
    else if (tab === "payments") loadPayments();
    else if (tab === "payouts") loadPayouts();
    else if (tab === "wallets") loadWallets();
    else if (tab === "reports") loadReports();
  }, [tab, analyticsDays, loadAnalytics, loadTeachers, loadUsers, loadBookings, loadPayments, loadPayouts, loadWallets, loadReports]);

  const handleApprove = async (t) => {
    try {
      await api.post(`/admin/teachers/${t.id}/approval`, { action: "approve" });
      toast.success(`${t.name} approved`);
      loadStats();
      loadTeachers(tab === "pending" ? "pending" : "all");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleReject = async (t, note) => {
    try {
      await api.post(`/admin/teachers/${t.id}/approval`, { action: "reject", note });
      toast.success(`${t.name} rejected`);
      loadStats();
      loadTeachers(tab === "pending" ? "pending" : "all");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleToggleFeatured = async (t, days) => {
    try {
      await api.post(`/admin/teachers/${t.id}/featured`, { days });
      toast.success(days > 0 ? `${t.name} featured for ${days} days` : `Removed featured`);
      loadStats();
      loadTeachers(tab === "pending" ? "pending" : "all");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleDeleteTeacher = async (t) => {
    if (!window.confirm(`Delete ${t.name} and ALL their data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${t.id}`);
      toast.success("Deleted");
      loadStats();
      loadTeachers(tab === "pending" ? "pending" : "all");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      loadStats();
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleDeleteBooking = async (b) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      toast.success("Deleted");
      loadStats();
      loadBookings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const handleReleaseBookingPayout = async (b) => {
    if (!window.confirm(
      `Release ₹${b.payout_amount} payout to ${b.teacher_name || "mentor"}? This credits their wallet immediately.`
    )) return;
    try {
      const { data } = await api.post(`/admin/payouts/${b.id}/mark-released`, { note: "" });
      toast.success(`Payout released — ₹${data.credited_inr || b.payout_amount} credited to mentor wallet`);
      loadBookings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Release failed");
    }
  };

  const handleReportStatusChange = async (report, newStatus) => {
    let note = "";
    if (newStatus === "resolved" || newStatus === "dismissed") {
      note = window.prompt(
        `Add a resolution note (optional) before marking this report as ${newStatus}:`,
        report.resolution_note || ""
      );
      if (note === null) return; // user cancelled prompt
    }
    try {
      await api.post(`/admin/session-reports/${report.id}/status`, { status: newStatus, note });
      toast.success(`Report marked as "${newStatus}"`);
      loadStats();
      loadReports();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="bg-peach min-h-screen" data-testid="admin-dashboard">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-14">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase" style={{ color: "#13A699" }}>
              Admin Dashboard
            </h1>
            <p className="text-sm mt-1" style={{ color: "#13A699AA" }}>
              Signed in as <span className="font-bold">{user?.email}</span>
            </p>
          </div>
          <button
            onClick={() => {
              loadStats();
              if (tab === "analytics") loadAnalytics(analyticsDays);
              else if (tab === "pending") loadTeachers("pending");
              else if (tab === "teachers") loadTeachers("all");
              else if (tab === "users") loadUsers();
              else if (tab === "bookings") loadBookings();
              else if (tab === "payments") loadPayments();
              else if (tab === "payouts") loadPayouts();
              else if (tab === "wallets") loadWallets();
            }}
            className="text-xs px-3 py-2 rounded-full border border-black/10 inline-flex items-center gap-1.5 bg-white"
            style={{ color: "#13A699" }}
            data-testid="admin-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Pending Approvals"
              value={stats.teachers.pending}
              hint={`${stats.teachers.approved} approved · ${stats.teachers.rejected} rejected`}
              color={stats.teachers.pending > 0 ? "#B45309" : "#13A699"}
            />
            <StatCard
              label="Total Revenue"
              value={inr(stats.revenue.total_inr)}
              hint={`Featured ${inr(stats.revenue.featured_inr)} · Demo ${inr(stats.revenue.demo_inr)}`}
            />
            <StatCard
              label="Users"
              value={stats.users.total}
              hint={`${stats.users.parents} explorers · ${stats.users.teachers} mentors`}
            />
            <StatCard
              label="Bookings"
              value={stats.bookings.regular + stats.bookings.demos_paid}
              hint={`${stats.bookings.regular} regular · ${stats.bookings.demos_paid} demos paid`}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-2 overflow-x-auto border-b border-black/10 pb-px">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            let count = null;
            if (t.key === "pending") count = stats?.teachers?.pending;
            else if (t.key === "reports") count = stats?.session_reports?.open;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`text-sm font-bold px-4 py-2 rounded-t-xl inline-flex items-center gap-2 whitespace-nowrap transition ${
                  active ? "bg-white border border-b-white border-black/10" : "text-[#13A699]/60 hover:text-[#13A699]"
                }`}
                style={active ? { color: "#13A699" } : undefined}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {count > 0 && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                    style={{
                      background: t.key === "reports" ? "#DC2626" : "#FFD708",
                      color: t.key === "reports" ? "#FFF7ED" : "#13A699",
                    }}
                    data-testid={`admin-tab-badge-${t.key}`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Panels */}
        <div className="mt-5">
          {loading && (
            <div className="text-center py-10 text-sm" style={{ color: "#13A699" }} data-testid="admin-loading">
              Loading…
            </div>
          )}

          {/* ANALYTICS */}
          {tab === "analytics" && !loading && analytics && (
            <AnalyticsPanel
              data={analytics}
              days={analyticsDays}
              onDaysChange={setAnalyticsDays}
            />
          )}

          {/* PENDING + TEACHERS */}
          {(tab === "pending" || tab === "teachers") && !loading && (
            <div className="space-y-3" data-testid={`admin-${tab}-list`}>
              {teachers.length === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center border border-black/5">
                  <p className="font-display" style={{ color: "#13A699" }}>
                    {tab === "pending" ? "🎉 No pending approvals!" : "No mentors yet"}
                  </p>
                </div>
              ) : (
                teachers.map((t) => (
                  <TeacherCard
                    key={t.id}
                    teacher={t}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onToggleFeatured={handleToggleFeatured}
                    onDelete={handleDeleteTeacher}
                  />
                ))
              )}
            </div>
          )}

          {/* USERS */}
          {tab === "users" && !loading && (
            <div data-testid="admin-users-panel">
              <div className="bg-white rounded-2xl p-4 border border-black/5 flex items-center gap-2 flex-wrap mb-3">
                <select
                  value={userFilter.role}
                  onChange={(e) => setUserFilter((f) => ({ ...f, role: e.target.value }))}
                  className="rounded-xl border border-black/10 px-3 py-2 text-sm bg-white"
                  style={{ color: "#13A699" }}
                  data-testid="admin-users-role-filter"
                >
                  <option value="all">All Roles</option>
                  <option value="parent">Explorers</option>
                  <option value="teacher">Mentors</option>
                  <option value="admin">Admins</option>
                </select>
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={userFilter.q}
                  onChange={(e) => setUserFilter((f) => ({ ...f, q: e.target.value }))}
                  className="flex-1 min-w-[200px] rounded-xl border border-black/10 px-3 py-2 text-sm bg-white"
                  style={{ color: "#13A699" }}
                  data-testid="admin-users-search"
                />
              </div>
              <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FFF7ED]">
                    <tr style={{ color: "#13A699" }}>
                      <th className="text-left px-4 py-2 font-bold">Name</th>
                      <th className="text-left px-4 py-2 font-bold">Email</th>
                      <th className="text-left px-4 py-2 font-bold">Role</th>
                      <th className="text-left px-4 py-2 font-bold">Subscription / Spend</th>
                      <th className="text-left px-4 py-2 font-bold">Created</th>
                      <th className="text-right px-4 py-2 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-6 text-center" style={{ color: "#13A69999" }}>No users match filter.</td></tr>
                    ) : users.map((u) => (
                      <tr key={u.id} className="border-t border-black/5" data-testid={`admin-user-row-${u.id}`}>
                        <td className="px-4 py-2" style={{ color: "#13A699" }}>{u.name || "—"}</td>
                        <td className="px-4 py-2" style={{ color: "#13A699" }}>{u.email}</td>
                        <td className="px-4 py-2"><Badge status={u.role} /></td>
                        <td className="px-4 py-2" data-testid={`admin-user-subscription-${u.id}`}>
                          <SubscriptionCell user={u} />
                        </td>
                        <td className="px-4 py-2 text-xs" style={{ color: "#13A69999" }}>{formatDate(u.created_at)}</td>
                        <td className="px-4 py-2 text-right">
                          {u.role !== "admin" && (
                            <button
                              onClick={() => handleDeleteUser(u)}
                              className="text-xs px-3 py-1 rounded-full bg-red-600 text-white font-bold inline-flex items-center gap-1"
                              data-testid={`admin-delete-user-${u.id}`}
                            >
                              <Trash2 className="h-3 w-3" /> Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* BOOKINGS */}
          {tab === "bookings" && !loading && (
            <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto" data-testid="admin-bookings-panel">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF7ED]">
                  <tr style={{ color: "#13A699" }}>
                    <th className="text-left px-4 py-2 font-bold">Date / Time</th>
                    <th className="text-left px-4 py-2 font-bold">Explorer</th>
                    <th className="text-left px-4 py-2 font-bold">Mentor</th>
                    <th className="text-left px-4 py-2 font-bold">Status</th>
                    <th className="text-left px-4 py-2 font-bold">Session</th>
                    <th className="text-left px-4 py-2 font-bold">Payout</th>
                    <th className="text-right px-4 py-2 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center" style={{ color: "#13A69999" }}>No bookings yet.</td></tr>
                  ) : bookings.map((b) => (
                    <tr key={b.id} className="border-t border-black/5" data-testid={`admin-booking-row-${b.id}`}>
                      <td className="px-4 py-2" style={{ color: "#13A699" }}>
                        <div className="font-bold">{b.date}</div>
                        <div className="text-xs" style={{ color: "#13A69999" }}>{b.start_time}–{b.end_time}</div>
                      </td>
                      <td className="px-4 py-2" style={{ color: "#13A699" }}>
                        <div>{b.parent_name}</div>
                        <div className="text-xs" style={{ color: "#13A69999" }}>{b.parent_email}</div>
                      </td>
                      <td className="px-4 py-2" style={{ color: "#13A699" }}>
                        <div>{b.teacher_name}</div>
                        <div className="text-xs" style={{ color: "#13A69999" }}>{b.teacher_email}</div>
                      </td>
                      <td className="px-4 py-2"><Badge status={b.status} /></td>
                      <td className="px-4 py-2" data-testid={`admin-booking-session-${b.id}`}>
                        <SessionStatusCell booking={b} />
                      </td>
                      <td className="px-4 py-2" data-testid={`admin-booking-payout-${b.id}`}>
                        <PayoutCell
                          booking={b}
                          onRelease={handleReleaseBookingPayout}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleDeleteBooking(b)}
                          className="text-xs px-3 py-1 rounded-full bg-red-600 text-white font-bold inline-flex items-center gap-1"
                          data-testid={`admin-delete-booking-${b.id}`}
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAYMENTS */}
          {tab === "payments" && !loading && (
            <div className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid="admin-payments-panel">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF7ED]">
                  <tr style={{ color: "#13A699" }}>
                    <th className="text-left px-4 py-2 font-bold">Type</th>
                    <th className="text-left px-4 py-2 font-bold">Amount</th>
                    <th className="text-left px-4 py-2 font-bold">User</th>
                    <th className="text-left px-4 py-2 font-bold">Mentor</th>
                    <th className="text-left px-4 py-2 font-bold">Status</th>
                    <th className="text-left px-4 py-2 font-bold">Razorpay ID</th>
                    <th className="text-left px-4 py-2 font-bold">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center" style={{ color: "#13A69999" }}>No payments yet.</td></tr>
                  ) : payments.map((p) => (
                    <tr key={`${p.type}-${p.id}`} className="border-t border-black/5" data-testid={`admin-payment-row-${p.id}`}>
                      <td className="px-4 py-2">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: p.type === "featured" ? "#FFD708" : "#13A699", color: p.type === "featured" ? "#13A699" : "#FFF7ED" }}>
                          {p.type === "featured" ? `Featured ${p.plan || ""}` : "Demo"}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-bold" style={{ color: "#13A699" }}>{inr(p.amount)}</td>
                      <td className="px-4 py-2" style={{ color: "#13A699" }}>
                        <div>{p.user_name || "—"}</div>
                        <div className="text-xs" style={{ color: "#13A69999" }}>{p.user_email}</div>
                      </td>
                      <td className="px-4 py-2" style={{ color: "#13A699" }}>
                        {p.teacher_name ? (
                          <>
                            <div>{p.teacher_name}</div>
                            <div className="text-xs" style={{ color: "#13A69999" }}>{p.teacher_email}</div>
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2"><Badge status={p.status} /></td>
                      <td className="px-4 py-2 text-[10px] font-mono" style={{ color: "#13A69999" }}>
                        {p.razorpay_payment_id || p.razorpay_order_id || "—"}
                      </td>
                      <td className="px-4 py-2 text-xs" style={{ color: "#13A69999" }}>{formatDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* PAYOUTS */}
          {tab === "payouts" && !loading && (
            <div data-testid="admin-payouts-panel" className="space-y-4">
              {/* Summary cards */}
              {payoutsSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard
                    label="Pending Payouts"
                    value={payoutsSummary.pending?.count || 0}
                    hint={`Total ${inr(payoutsSummary.pending?.total_payout || 0)}`}
                    color="#B45309"
                  />
                  <StatCard
                    label="Released Payouts"
                    value={payoutsSummary.released?.count || 0}
                    hint={`Total ${inr(payoutsSummary.released?.total_payout || 0)}`}
                  />
                  <StatCard
                    label="Pending Commission"
                    value={inr(payoutsSummary.pending?.total_commission || 0)}
                    hint="Platform fee held"
                  />
                  <StatCard
                    label="Released Commission"
                    value={inr(payoutsSummary.released?.total_commission || 0)}
                    hint="Platform fee earned"
                  />
                </div>
              )}

              {/* Filter */}
              <div className="bg-white rounded-2xl p-3 border border-black/5 flex items-center gap-2 flex-wrap" data-testid="payouts-filter">
                {[
                  { key: "pending", label: "Pending" },
                  { key: "released", label: "Released" },
                  { key: "n_a", label: "No Payout" },
                  { key: "all", label: "All" },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setPayoutStatus(f.key)}
                    className="text-xs font-bold px-3 py-1.5 rounded-full"
                    style={{
                      background: payoutStatus === f.key ? "#FFD708" : "#FFF7ED",
                      color: "#13A699",
                      border: payoutStatus === f.key ? "1px solid #FFD708" : "1px solid #13A69922",
                    }}
                    data-testid={`payouts-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Table */}
              <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
                <table className="w-full text-sm min-w-[1100px]">
                  <thead className="bg-[#FFF7ED]">
                    <tr style={{ color: "#13A699" }}>
                      <th className="text-left px-3 py-2 font-bold">Mentor</th>
                      <th className="text-left px-3 py-2 font-bold">Explorer</th>
                      <th className="text-left px-3 py-2 font-bold">Session</th>
                      <th className="text-left px-3 py-2 font-bold">Status</th>
                      <th className="text-right px-3 py-2 font-bold">Resched.</th>
                      <th className="text-right px-3 py-2 font-bold">Fees</th>
                      <th className="text-right px-3 py-2 font-bold">Payout</th>
                      <th className="text-right px-3 py-2 font-bold">Commission</th>
                      <th className="text-right px-3 py-2 font-bold">Penalty</th>
                      <th className="text-right px-3 py-2 font-bold">Refund</th>
                      <th className="text-right px-3 py-2 font-bold">Bonus</th>
                      <th className="text-left px-3 py-2 font-bold">Payout Status</th>
                      <th className="text-left px-3 py-2 font-bold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.length === 0 ? (
                      <tr><td colSpan={13} className="px-4 py-6 text-center" style={{ color: "#13A69999" }}>No matching sessions.</td></tr>
                    ) : payouts.map((r) => (
                      <tr key={r.booking_id} className="border-t border-black/5" data-testid={`payout-row-${r.booking_id}`}>
                        <td className="px-3 py-2" style={{ color: "#13A699" }}>
                          <div className="font-bold">{r.mentor?.name || "—"}</div>
                          <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.mentor?.email}</div>
                        </td>
                        <td className="px-3 py-2" style={{ color: "#13A699" }}>
                          <div className="font-bold">{r.explorer?.parent_name || "—"}</div>
                          <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.explorer?.parent_email}</div>
                          {r.explorer?.child_name && (
                            <div className="text-[11px]">For: {r.explorer.child_name}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs" style={{ color: "#13A699" }}>
                          <div>{r.session?.date}</div>
                          <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.session?.start_time}–{r.session?.end_time}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap"
                            style={{
                              background: r.status === "completed" ? "#13A699" : "#DC2626",
                              color: "#FFF7ED",
                            }}
                          >
                            {r.status_label || r.status}
                          </span>
                          {r.cancelled_by_role && (
                            <div className="text-[10px] mt-0.5" style={{ color: "#13A69999" }}>
                              by {r.cancelled_by_role}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right" style={{ color: "#13A699" }}>{r.reschedule_count}</td>
                        <td className="px-3 py-2 text-right" style={{ color: "#13A699" }}>{inr(r.fees_snapshot)}</td>
                        <td className="px-3 py-2 text-right font-bold" style={{ color: "#13A699" }}>{inr(r.payout_amount)}</td>
                        <td className="px-3 py-2 text-right" style={{ color: "#13A69999" }}>{inr(r.commission_amount)}</td>
                        <td className="px-3 py-2 text-right" style={{ color: r.penalty_amount ? "#DC2626" : "#13A69999" }}>
                          {r.penalty_amount ? `-${inr(r.penalty_amount)}` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right" style={{ color: "#13A69999" }}>{r.parent_refund_amount ? inr(r.parent_refund_amount) : "—"}</td>
                        <td className="px-3 py-2 text-right" style={{ color: "#13A69999" }}>{r.parent_bonus_amount ? inr(r.parent_bonus_amount) : "—"}</td>
                        <td className="px-3 py-2">
                          <span
                            className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                            style={{
                              background: r.payout_status === "released" ? "#13A699" : r.payout_status === "pending" ? "#FFD708" : "#E5E7EB",
                              color: r.payout_status === "released" ? "#FFF7ED" : "#13A699",
                            }}
                          >
                            {r.payout_status}
                          </span>
                          {r.payout_released_at && (
                            <div className="text-[10px] mt-0.5" style={{ color: "#13A69999" }}>{formatDate(r.payout_released_at)}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.payout_status === "pending" && r.payout_amount > 0 ? (
                            <button
                              onClick={() => handleMarkReleased(r)}
                              className="btn-yellow text-[11px] px-3 py-1.5"
                              data-testid={`mark-released-${r.booking_id}`}
                            >
                              Mark Released
                            </button>
                          ) : (
                            <span className="text-[10px]" style={{ color: "#13A69999" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Course Cancellations sub-section */}
              <div className="mt-6">
                <h3 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }} data-testid="course-cancellations-heading">
                  Course Cancellations · Charges Owed by Mentors
                </h3>
                <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
                  When a mentor cancels an entire course, the platform charges 20% of the course fee and refunds the parent in full.
                </p>

                {courseCancelsSummary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <StatCard
                      label="Pending Charges"
                      value={courseCancelsSummary.pending?.count || 0}
                      hint={`Owed ${inr(courseCancelsSummary.pending?.total_charge || 0)}`}
                      color="#B45309"
                      testId="course-cancels-stat-pending-charge"
                    />
                    <StatCard
                      label="Collected Charges"
                      value={courseCancelsSummary.collected?.count || 0}
                      hint={`Total ${inr(courseCancelsSummary.collected?.total_charge || 0)}`}
                      testId="course-cancels-stat-collected-charge"
                    />
                    <StatCard
                      label="Pending Parent Refunds"
                      value={inr(courseCancelsSummary.pending?.total_refund || 0)}
                      hint="Refundable to parents"
                      testId="course-cancels-stat-pending-refund"
                    />
                    <StatCard
                      label="Avg Charge"
                      value={
                        courseCancelsSummary.pending?.count
                          ? inr(Math.round((courseCancelsSummary.pending.total_charge || 0) / courseCancelsSummary.pending.count))
                          : inr(0)
                      }
                      hint="Per cancelled course"
                      testId="course-cancels-stat-avg-charge"
                    />
                  </div>
                )}

                <div className="mt-3 bg-white rounded-2xl p-3 border border-black/5 flex items-center gap-2 flex-wrap" data-testid="course-cancels-filter">
                  {[
                    { key: "pending", label: "Pending" },
                    { key: "collected", label: "Collected" },
                    { key: "all", label: "All" },
                  ].map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setCourseCancelStatus(f.key)}
                      className="text-xs font-bold px-3 py-1.5 rounded-full"
                      style={{
                        background: courseCancelStatus === f.key ? "#FFD708" : "#FFF7ED",
                        color: "#13A699",
                        border: courseCancelStatus === f.key ? "1px solid #FFD708" : "1px solid #13A69922",
                      }}
                      data-testid={`course-cancels-filter-${f.key}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="mt-3 bg-white rounded-2xl border border-black/5 overflow-x-auto">
                  <table className="w-full text-sm min-w-[1000px]">
                    <thead className="bg-[#FFF7ED]">
                      <tr style={{ color: "#13A699" }}>
                        <th className="text-left px-3 py-2 font-bold">Mentor</th>
                        <th className="text-left px-3 py-2 font-bold">Explorer</th>
                        <th className="text-left px-3 py-2 font-bold">Course</th>
                        <th className="text-right px-3 py-2 font-bold">Course Fee</th>
                        <th className="text-right px-3 py-2 font-bold">Charge (20%)</th>
                        <th className="text-right px-3 py-2 font-bold">Refund</th>
                        <th className="text-left px-3 py-2 font-bold">Cancelled At</th>
                        <th className="text-left px-3 py-2 font-bold">Charge Status</th>
                        <th className="text-left px-3 py-2 font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courseCancels.length === 0 ? (
                        <tr><td colSpan={9} className="px-4 py-6 text-center" style={{ color: "#13A69999" }}>No course cancellations.</td></tr>
                      ) : courseCancels.map((r) => (
                        <tr key={r.enrollment_id} className="border-t border-black/5" data-testid={`course-cancel-row-${r.enrollment_id}`}>
                          <td className="px-3 py-2" style={{ color: "#13A699" }}>
                            <div className="font-bold">{r.mentor?.name || "—"}</div>
                            <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.mentor?.email}</div>
                          </td>
                          <td className="px-3 py-2" style={{ color: "#13A699" }}>
                            <div className="font-bold">{r.explorer?.parent_name || "—"}</div>
                            <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.explorer?.parent_email}</div>
                            {r.explorer?.child_name && (
                              <div className="text-[11px]">For: {r.explorer.child_name}</div>
                            )}
                          </td>
                          <td className="px-3 py-2" style={{ color: "#13A699" }}>
                            <div className="font-bold">{r.course_title}</div>
                            <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.course_subject} · {r.num_sessions} sessions</div>
                            {r.cancel_reason && (
                              <div className="text-[11px] italic mt-0.5" style={{ color: "#13A69988" }}>“{r.cancel_reason}”</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right" style={{ color: "#13A699" }}>{inr(r.fees_snapshot)}</td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: "#DC2626" }}>{inr(r.course_charge_amount)}</td>
                          <td className="px-3 py-2 text-right" style={{ color: "#13A699" }}>{inr(r.parent_refund_amount)}</td>
                          <td className="px-3 py-2 text-xs" style={{ color: "#13A69999" }}>
                            {r.cancelled_at ? formatDate(r.cancelled_at) : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                              style={{
                                background: r.course_charge_status === "collected" ? "#13A699" : r.course_charge_status === "pending" ? "#FFD708" : "#E5E7EB",
                                color: r.course_charge_status === "collected" ? "#FFF7ED" : "#13A699",
                              }}
                            >
                              {r.course_charge_status}
                            </span>
                            {r.course_charge_collected_at && (
                              <div className="text-[10px] mt-0.5" style={{ color: "#13A69999" }}>{formatDate(r.course_charge_collected_at)}</div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {r.course_charge_status === "pending" && r.course_charge_amount > 0 ? (
                              <button
                                onClick={() => handleMarkCharged(r)}
                                className="btn-yellow text-[11px] px-3 py-1.5"
                                data-testid={`mark-collected-${r.enrollment_id}`}
                              >
                                Mark Collected
                              </button>
                            ) : (
                              <span className="text-[10px]" style={{ color: "#13A69999" }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* WALLETS TAB */}
          {tab === "wallets" && !loading && (
            <div data-testid="admin-wallets-panel" className="space-y-4">
              {/* Settlement requests */}
              <div>
                <h3 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }} data-testid="settlement-requests-heading">
                  Mentor Settlement Requests
                </h3>
                <div className="mt-2 bg-white rounded-2xl p-3 border border-black/5 flex items-center gap-2 flex-wrap" data-testid="settlements-filter">
                  {["pending", "paid", "rejected", "cancelled", "all"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setSettlementStatus(s)}
                      className="text-xs font-bold px-3 py-1.5 rounded-full capitalize"
                      style={{
                        background: settlementStatus === s ? "#FFD708" : "#FFF7ED",
                        color: "#13A699",
                        border: settlementStatus === s ? "1px solid #FFD708" : "1px solid #13A69922",
                      }}
                      data-testid={`settlements-filter-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div className="mt-3 bg-white rounded-2xl border border-black/5 overflow-x-auto">
                  <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-[#FFF7ED]">
                      <tr style={{ color: "#13A699" }}>
                        <th className="text-left px-3 py-2 font-bold">Mentor</th>
                        <th className="text-right px-3 py-2 font-bold">Amount</th>
                        <th className="text-left px-3 py-2 font-bold">Bank</th>
                        <th className="text-left px-3 py-2 font-bold">Requested</th>
                        <th className="text-left px-3 py-2 font-bold">Status</th>
                        <th className="text-left px-3 py-2 font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlementReqs.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-6 text-center" style={{ color: "#13A69999" }}>No settlement requests.</td></tr>
                      ) : settlementReqs.map((r) => (
                        <tr key={r.id} className="border-t border-black/5" data-testid={`settlement-req-row-${r.id}`}>
                          <td className="px-3 py-2" style={{ color: "#13A699" }}>
                            <div className="font-bold">{r.mentor_name || "—"}</div>
                            <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.mentor_email}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: "#13A699" }}>{inr(r.amount)}</td>
                          <td className="px-3 py-2 text-xs" style={{ color: "#13A699" }}>
                            {r.bank_snapshot ? (
                              <>
                                <div>{r.bank_snapshot.account_holder_name}</div>
                                <div className="text-[11px]" style={{ color: "#13A69999" }}>
                                  ****{(r.bank_snapshot.account_number || "").slice(-4)} · {r.bank_snapshot.ifsc}
                                </div>
                                {r.bank_snapshot.upi_id && <div className="text-[10px]">UPI: {r.bank_snapshot.upi_id}</div>}
                              </>
                            ) : <span style={{ color: "#13A69999" }}>—</span>}
                          </td>
                          <td className="px-3 py-2 text-[11px]" style={{ color: "#13A69999" }}>{r.created_at ? formatDate(r.created_at) : "—"}</td>
                          <td className="px-3 py-2">
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{
                              background: r.status === "paid" ? "#13A699" : r.status === "rejected" ? "#DC2626" : r.status === "cancelled" ? "#E5E7EB" : "#FFD708",
                              color: r.status === "paid" || r.status === "rejected" ? "#FFF7ED" : "#13A699",
                            }}>{r.status}</span>
                          </td>
                          <td className="px-3 py-2">
                            {r.status === "pending" ? (
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => handleMarkSettlementPaid(r)} className="btn-yellow text-[11px] px-3 py-1.5" data-testid={`mark-paid-${r.id}`}>Mark Paid</button>
                                <button onClick={() => handleRejectSettlement(r)} className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-red-600 text-red-600 hover:bg-red-50" data-testid={`reject-${r.id}`}>Reject</button>
                              </div>
                            ) : <span className="text-[10px]" style={{ color: "#13A69999" }}>—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* All wallets list */}
              <div>
                <h3 className="font-display text-lg font-bold uppercase mt-4" style={{ color: "#13A699" }} data-testid="wallets-heading">
                  All Wallets
                </h3>
                <div className="mt-2 bg-white rounded-2xl p-3 border border-black/5 flex items-center gap-2 flex-wrap" data-testid="wallets-role-filter">
                  {[
                    { k: "", l: "All" },
                    { k: "parent", l: "Explorers" },
                    { k: "teacher", l: "Mentors" },
                  ].map((f) => (
                    <button
                      key={f.k || "all"}
                      onClick={() => setWalletRoleFilter(f.k)}
                      className="text-xs font-bold px-3 py-1.5 rounded-full"
                      style={{
                        background: walletRoleFilter === f.k ? "#FFD708" : "#FFF7ED",
                        color: "#13A699",
                        border: walletRoleFilter === f.k ? "1px solid #FFD708" : "1px solid #13A69922",
                      }}
                      data-testid={`wallets-role-${f.k || "all"}`}
                    >
                      {f.l}
                    </button>
                  ))}
                </div>
                <div className="mt-3 bg-white rounded-2xl border border-black/5 overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-[#FFF7ED]">
                      <tr style={{ color: "#13A699" }}>
                        <th className="text-left px-3 py-2 font-bold">User</th>
                        <th className="text-left px-3 py-2 font-bold">Role</th>
                        <th className="text-right px-3 py-2 font-bold">Balance</th>
                        <th className="text-left px-3 py-2 font-bold">Bank</th>
                        <th className="text-left px-3 py-2 font-bold">Updated</th>
                        <th className="text-left px-3 py-2 font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wallets.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-6 text-center" style={{ color: "#13A69999" }}>No wallets yet.</td></tr>
                      ) : wallets.map((w) => (
                        <tr key={w.user_id} className="border-t border-black/5" data-testid={`wallet-row-${w.user_id}`}>
                          <td className="px-3 py-2" style={{ color: "#13A699" }}>
                            <div className="font-bold">{w.name || "—"}</div>
                            <div className="text-[11px]" style={{ color: "#13A69999" }}>{w.email}</div>
                          </td>
                          <td className="px-3 py-2 text-xs uppercase font-bold" style={{ color: "#13A699" }}>{w.role}</td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: w.balance < 0 ? "#DC2626" : "#13A699" }}>{inr(w.balance)}</td>
                          <td className="px-3 py-2 text-xs" style={{ color: w.has_bank_details ? "#13A699" : "#13A69999" }}>
                            {w.has_bank_details ? "✓ Saved" : "—"}
                          </td>
                          <td className="px-3 py-2 text-[11px]" style={{ color: "#13A69999" }}>{w.updated_at ? formatDate(w.updated_at) : "—"}</td>
                          <td className="px-3 py-2">
                            {w.role === "parent" ? (
                              <button onClick={() => handleTopup(w)} className="btn-yellow text-[11px] px-3 py-1.5" data-testid={`topup-${w.user_id}`}>+ Top up</button>
                            ) : (
                              <span className="text-[10px]" style={{ color: "#13A69999" }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "mentor_subs" && (
            <AdminMentorSubscriptionsPanel />
          )}

          {tab === "reports" && !loading && (
            <div className="rounded-3xl border border-black/10 bg-white p-4 sm:p-6" data-testid="reports-panel">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h2 className="font-display text-lg font-bold flex items-center gap-2" style={{ color: "#13A699" }}>
                  <AlertTriangle className="h-5 w-5" /> Session Reports
                </h2>
                <div className="ml-auto flex items-center gap-2">
                  <label className="text-xs font-semibold" style={{ color: "#13A699" }}>Filter:</label>
                  <select
                    value={reportsFilter}
                    onChange={(e) => setReportsFilter(e.target.value)}
                    className="rounded-xl border border-[#13A69955] bg-white px-3 py-1.5 text-xs"
                    data-testid="reports-filter"
                  >
                    <option value="open">Open</option>
                    <option value="in_review">In review</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                    <option value="">All</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide" style={{ color: "#13A69999" }}>
                      <th className="px-3 py-2">Filed</th>
                      <th className="px-3 py-2">Reporter</th>
                      <th className="px-3 py-2">Against</th>
                      <th className="px-3 py-2">Category</th>
                      <th className="px-3 py-2">Concern raised</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Booking</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center" style={{ color: "#13A69999" }} data-testid="reports-empty">
                          No session reports for the selected filter.
                        </td>
                      </tr>
                    ) : reports.map((r) => (
                      <tr key={r.id} className="border-t border-black/5 align-top" data-testid={`report-row-${r.id}`}>
                        <td className="px-3 py-2 text-[11px]" style={{ color: "#13A69999" }}>{formatDate(r.created_at)}</td>
                        <td className="px-3 py-2 text-xs" style={{ color: "#13A699" }}>
                          <div className="uppercase font-bold">{r.reporter_role}</div>
                          <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.reporter_name || `${(r.reporter_id || "").slice(0, 8)}…`}</div>
                        </td>
                        <td className="px-3 py-2 text-xs" style={{ color: "#13A699" }}>
                          <div className="uppercase font-bold">{r.reported_role}</div>
                          <div className="text-[11px]" style={{ color: "#13A69999" }}>{r.reported_name || `${(r.reported_id || "").slice(0, 8)}…`}</div>
                        </td>
                        <td className="px-3 py-2 text-xs font-bold uppercase" style={{ color: "#13A699" }}>{r.category}</td>
                        <td className="px-3 py-2 text-xs max-w-md whitespace-pre-wrap" style={{ color: "#13A699" }}>
                          {r.reason}
                          {r.resolution_note && (
                            <div className="mt-1 text-[10px] italic" style={{ color: "#13A69988" }}>
                              Resolution: {r.resolution_note}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="text-[10px] font-bold uppercase px-2 py-1 rounded-full"
                            style={{
                              background: r.status === "open" ? "#DC2626" : r.status === "resolved" ? "#13A699" : "#FBA31255",
                              color: r.status === "open" || r.status === "resolved" ? "#FFF7ED" : "#13A699",
                            }}
                          >{r.status}</span>
                        </td>
                        <td className="px-3 py-2 text-[11px]" style={{ color: "#13A69999" }}>{r.booking_id?.slice(0, 8)}…</td>
                        <td className="px-3 py-2 text-right">
                          <ReportActions
                            report={r}
                            onChange={handleReportStatusChange}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}

// -------------------- Analytics Panel --------------------
const CHART_COLORS = ["#13A699", "#FFD708", "#F97316", "#8B5CF6", "#EF4444"];
const RANGE_OPTIONS = [7, 30, 90, 180];

function downloadCSV(filename, headers, rows) {
  // Wrap each value in quotes and escape embedded quotes. Numbers/strings both fine.
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = [headers.map(esc).join(",")];
  for (const r of rows) lines.push(r.map(esc).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CsvButton({ onClick, testId, label = "CSV" }) {
  return (
    <button
      onClick={onClick}
      className="text-[11px] font-bold px-2.5 py-1 rounded-full border inline-flex items-center gap-1"
      style={{ borderColor: "#13A69933", color: "#13A699", background: "#FFF7ED" }}
      data-testid={testId}
      title="Download as CSV"
    >
      ⤓ {label}
    </button>
  );
}

function AnalyticsPanel({ data, days, onDaysChange }) {
  const totals = data?.totals || {};
  const revenue = (data?.revenue_trend || []).map((r) => ({
    ...r,
    label: (r.date || "").slice(5), // MM-DD
  }));
  const growth = (data?.user_growth || []).map((r) => ({
    ...r,
    label: (r.date || "").slice(5),
  }));
  const topCourses = (data?.top_courses || []).map((c, idx) => ({
    ...c,
    short: (c.title || "").length > 24 ? c.title.slice(0, 22) + "…" : c.title,
    fill: CHART_COLORS[idx % CHART_COLORS.length],
  }));
  const funnel = data?.enrollment_funnel || [];

  return (
    <div className="space-y-5" data-testid="admin-analytics-panel">
      {/* Range picker */}
      <div className="bg-white rounded-2xl p-3 border border-black/5 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase" style={{ color: "#13A699AA" }}>
          Time Window:
        </span>
        {RANGE_OPTIONS.map((d) => {
          const active = days === d;
          return (
            <button
              key={d}
              onClick={() => onDaysChange(d)}
              className="text-xs font-bold px-3 py-1.5 rounded-full border"
              style={{
                background: active ? "#13A699" : "#FFF7ED",
                color: active ? "#FFF7ED" : "#13A699",
                borderColor: active ? "#13A699" : "#13A69922",
              }}
              data-testid={`analytics-range-${d}`}
            >
              {d} days
            </button>
          );
        })}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label={`Revenue · ${days}d`}
          value={`₹${(totals.total_inr || 0).toLocaleString("en-IN")}`}
          hint={`Featured ₹${(totals.featured_inr || 0).toLocaleString("en-IN")} · Demo ₹${(totals.demo_inr || 0).toLocaleString("en-IN")} · Course ₹${(totals.course_inr || 0).toLocaleString("en-IN")}`}
          testId="analytics-kpi-revenue"
        />
        <StatCard
          label={`New Users · ${days}d`}
          value={(totals.new_users || 0).toLocaleString("en-IN")}
          hint={`${totals.new_parents || 0} explorers · ${totals.new_teachers || 0} mentors`}
          testId="analytics-kpi-users"
        />
        <StatCard
          label="Active Mentors"
          value={data.active_mentors ?? 0}
          hint="Bookings or paid enrollments in window"
          testId="analytics-kpi-active-mentors"
        />
        <StatCard
          label="Platform Rating"
          value={data.platform_rating != null ? `${data.platform_rating.toFixed(2)} ★` : "—"}
          hint={`${data.rated_mentors || 0} rated mentors`}
          testId="analytics-kpi-rating"
        />
      </div>

      {/* Revenue trend */}
      <div className="bg-white rounded-2xl p-4 border border-black/5" data-testid="analytics-revenue-chart">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <TrendingUp className="h-4 w-4" /> Revenue Trend (₹)
          </h3>
          <CsvButton
            testId="analytics-export-revenue"
            label="Revenue CSV"
            onClick={() =>
              downloadCSV(
                `rate-ed_revenue_last_${days}d.csv`,
                ["date", "featured", "demo", "course", "total"],
                revenue.map((r) => [r.date, r.featured, r.demo, r.course, r.total]),
              )
            }
          />
        </div>
        {revenue.length === 0 ? (
          <p className="text-sm mt-3" style={{ color: "#13A69988" }}>No paid transactions in this window.</p>
        ) : (
          <div className="mt-3" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue}>
                <defs>
                  <linearGradient id="revTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#13A699" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#13A699" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#13A69922" />
                <XAxis dataKey="label" tick={{ fill: "#13A699", fontSize: 11 }} />
                <YAxis tick={{ fill: "#13A699", fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "#FFF7ED", border: "1px solid #13A69933", borderRadius: 12 }}
                  labelStyle={{ color: "#13A699", fontWeight: 700 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="total" stroke="#13A699" fill="url(#revTotal)" name="Total" strokeWidth={2} />
                <Area type="monotone" dataKey="featured" stroke="#FFD708" fill="transparent" name="Featured" strokeWidth={1.5} />
                <Area type="monotone" dataKey="demo" stroke="#F97316" fill="transparent" name="Demo" strokeWidth={1.5} />
                <Area type="monotone" dataKey="course" stroke="#8B5CF6" fill="transparent" name="Course" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* User growth */}
      <div className="bg-white rounded-2xl p-4 border border-black/5" data-testid="analytics-user-growth-chart">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <Users className="h-4 w-4" /> New Signups (Daily)
          </h3>
          <CsvButton
            testId="analytics-export-signups"
            label="Signups CSV"
            onClick={() =>
              downloadCSV(
                `rate-ed_signups_last_${days}d.csv`,
                ["date", "explorers", "mentors", "total"],
                growth.map((r) => [r.date, r.parents, r.teachers, r.total]),
              )
            }
          />
        </div>
        {growth.length === 0 ? (
          <p className="text-sm mt-3" style={{ color: "#13A69988" }}>No new signups in this window.</p>
        ) : (
          <div className="mt-3" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#13A69922" />
                <XAxis dataKey="label" tick={{ fill: "#13A699", fontSize: 11 }} />
                <YAxis tick={{ fill: "#13A699", fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "#FFF7ED", border: "1px solid #13A69933", borderRadius: 12 }}
                  labelStyle={{ color: "#13A699", fontWeight: 700 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="parents" stackId="a" fill="#13A699" name="Explorers" />
                <Bar dataKey="teachers" stackId="a" fill="#FFD708" name="Mentors" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Enrollment funnel */}
        <div className="bg-white rounded-2xl p-4 border border-black/5" data-testid="analytics-funnel-chart">
          <h3 className="text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <BookOpen className="h-4 w-4" /> Enrollment Funnel (All-time)
          </h3>
          <div className="mt-3" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#13A69922" />
                <XAxis type="number" tick={{ fill: "#13A699", fontSize: 11 }} />
                <YAxis type="category" dataKey="stage" tick={{ fill: "#13A699", fontSize: 11 }} width={130} />
                <Tooltip
                  contentStyle={{ background: "#FFF7ED", border: "1px solid #13A69933", borderRadius: 12 }}
                  labelStyle={{ color: "#13A699", fontWeight: 700 }}
                />
                <Bar dataKey="value" fill="#13A699" radius={[0, 8, 8, 0]}>
                  {funnel.map((entry, idx) => (
                    <Cell key={entry.stage} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top courses */}
        <div className="bg-white rounded-2xl p-4 border border-black/5" data-testid="analytics-top-courses">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2" style={{ color: "#13A699" }}>
              <Crown className="h-4 w-4" /> Top Courses (Paid Enrollments)
            </h3>
            <CsvButton
              testId="analytics-export-top-courses"
              label="Top Courses CSV"
              onClick={() =>
                downloadCSV(
                  `rate-ed_top_courses.csv`,
                  ["rank", "course_id", "title", "mentor", "enrollments", "revenue_inr"],
                  topCourses.map((c, i) => [i + 1, c.course_id, c.title, c.teacher_name, c.enrollments, c.revenue]),
                )
              }
            />
          </div>
          {topCourses.length === 0 ? (
            <p className="text-sm mt-3" style={{ color: "#13A69988" }}>No paid enrollments yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-black/5">
              {topCourses.map((c, idx) => (
                <li key={c.course_id} className="py-2.5 flex items-center gap-3" data-testid={`analytics-top-course-${idx}`}>
                  <span
                    className="inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold shrink-0"
                    style={{ background: c.fill, color: idx === 1 ? "#13A699" : "#FFF7ED" }}
                  >
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate" style={{ color: "#13A699" }}>{c.short}</div>
                    <div className="text-[11px]" style={{ color: "#13A69999" }}>{c.teacher_name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-sm" style={{ color: "#13A699" }}>{c.enrollments} enrol.</div>
                    <div className="text-[11px]" style={{ color: "#13A69999" }}>₹{(c.revenue || 0).toLocaleString("en-IN")}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


// -------------------- Admin bookings helpers --------------------
function _fmtShortTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function SessionStatusCell({ booking }) {
  const starts = booking.session_starts || {};
  const punches = booking.punch_ins || {};
  const bothStarted = Boolean(starts.parent && starts.teacher);
  const bothCompleted = Boolean(punches.parent && punches.teacher);
  const partialStart = Boolean(starts.parent || starts.teacher);
  const partialPunch = Boolean(punches.parent || punches.teacher);

  if (bothCompleted || booking.status === "completed") {
    return (
      <div className="flex flex-col gap-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full w-fit"
          style={{ background: "#13A699", color: "#FFF7ED" }}>
          Completed
        </span>
        {booking.completed_at && (
          <span className="text-[10px]" style={{ color: "#13A69999" }}>{_fmtShortTs(booking.completed_at)}</span>
        )}
      </div>
    );
  }
  if (bothStarted) {
    return (
      <div className="flex flex-col gap-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full w-fit"
          style={{ background: "#22C55E", color: "#FFF7ED" }}>
          Session Started
        </span>
        {booking.session_started_at && (
          <span className="text-[10px]" style={{ color: "#13A69999" }}>{_fmtShortTs(booking.session_started_at)}</span>
        )}
        {partialPunch && (
          <span className="text-[10px]" style={{ color: "#13A69988" }}>
            End: {punches.teacher ? "M ✓ " : ""}{punches.parent ? "E ✓" : ""}
          </span>
        )}
      </div>
    );
  }
  if (partialStart) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full w-fit"
        style={{ background: "#FBBF24", color: "#13A699" }}>
        {starts.teacher ? "Mentor" : "Explorer"} started
      </span>
    );
  }
  return (
    <span className="text-[10px]" style={{ color: "#13A69988" }}>Not started</span>
  );
}

function PayoutCell({ booking, onRelease }) {
  const amt = Number(booking.payout_amount || 0);
  const status = booking.payout_status || "n/a";
  if (amt <= 0 || status === "n/a") {
    return <span className="text-[11px]" style={{ color: "#13A69988" }}>—</span>;
  }
  if (status === "released") {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full w-fit"
          style={{ background: "#13A699", color: "#FFF7ED" }}>
          Released
        </span>
        <span className="text-xs font-bold" style={{ color: "#13A699" }}>₹{amt.toLocaleString("en-IN")}</span>
      </div>
    );
  }
  // pending
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full w-fit"
        style={{ background: "#FBBF24", color: "#13A699" }}>
        Pending
      </span>
      <span className="text-xs font-bold" style={{ color: "#13A699" }}>₹{amt.toLocaleString("en-IN")}</span>
      <button
        onClick={() => onRelease(booking)}
        className="text-[10px] font-bold px-2 py-0.5 rounded-full w-fit"
        style={{ background: "#13A699", color: "#FFF7ED" }}
        data-testid={`admin-release-payout-${booking.id}`}
      >
        Release now
      </button>
    </div>
  );
}

function SubscriptionCell({ user }) {
  const s = user.subscription;
  if (!s) return <span className="text-[11px]" style={{ color: "#13A69988" }}>—</span>;
  if (user.role === "teacher") {
    if (s.type === "featured" && s.plan) {
      const untilDate = s.until ? new Date(s.until) : null;
      const days = untilDate ? Math.max(0, Math.round((untilDate - Date.now()) / (1000 * 60 * 60 * 24))) : null;
      return (
        <div className="flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full w-fit"
            style={{ background: "#FFD708", color: "#13A699" }}>
            Featured {String(s.plan).toUpperCase()}
          </span>
          {untilDate && (
            <span className="text-[10px]" style={{ color: "#13A69999" }}>
              Until {untilDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              {days != null && ` · ${days}d left`}
            </span>
          )}
        </div>
      );
    }
    return <span className="text-[11px]" style={{ color: "#13A69988" }}>None</span>;
  }
  if (user.role === "parent") {
    const spent = Number(s.total_spent_inr || 0);
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-bold" style={{ color: "#13A699" }}>
          ₹{spent.toLocaleString("en-IN")}
        </span>
        <span className="text-[10px]" style={{ color: "#13A69988" }}>Total spent</span>
      </div>
    );
  }
  return <span className="text-[11px]" style={{ color: "#13A69988" }}>—</span>;
}

function ReportActions({ report, onChange }) {
  const options = [
    { k: "in_review", label: "In Review", bg: "#FBBF24" },
    { k: "resolved", label: "Resolved", bg: "#13A699" },
    { k: "dismissed", label: "Dismiss", bg: "#6B7280" },
    { k: "open", label: "Re-open", bg: "#DC2626" },
  ].filter((o) => o.k !== report.status);
  return (
    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
      {options.map((o) => (
        <button
          key={o.k}
          onClick={() => onChange(report, o.k)}
          className="text-[10px] font-bold uppercase px-2 py-1 rounded-full"
          style={{ background: o.bg, color: "#FFF7ED" }}
          data-testid={`report-action-${o.k}-${report.id}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
